/// <reference types="@types/applepayjs" />
import { useCallback, useEffect, useRef, useState } from 'react';

import { IApplePayDetails } from 'features/payment/types';
import { useStoreContext } from 'features/store';
import { STORE_UNAVAILABLE } from 'features/store/constants';
import { useRestaurantStatusChecker } from 'features/store/hooks/use-restaurant-checker';

import { useApplePayValidationLazyQuery } from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { fullBrandName, getConfigValue } from 'utils/environment';
import { sanitizeAlphanumeric } from 'utils/form';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import logger from 'utils/logger';
import { buildFormattedBillingAddress } from 'utils/native-actions';

import { useCart } from '../../../hooks/use-cart';

import { applePayCardDetails } from './apple-pay-payment-details';
import { ApplePayFailureReason, ApplePayOutcome, IApplePayResult, IUseApplePay } from './types';
import {
  IApplePayWebCapability,
  getApplePayWebCapability,
  useApplePayWebCapability,
} from './use-can-use-apple-pay.web';
import useFormatApplePayCartPayload from './use-format-apple-pay-cart-payload';

declare global {
  interface Window {
    ApplePaySession: typeof ApplePaySession | undefined;
  }
}

export const APPLE_PAY_WEB_LIFECYCLE_LOG = 'Apple Pay Web lifecycle';

type ApplePayLifecycle = 'request-start' | 'sheet-start' | 'terminal';

interface IApplePayRequest {
  ownsSession: boolean;
  session?: ApplePaySession;
  settled: boolean;
}

interface IApplePayLifecycleLog {
  lifecycle: ApplePayLifecycle;
  outcome?: ApplePayOutcome;
  reason?: ApplePayFailureReason;
  capability?: IApplePayWebCapability;
}

export default function useApplePay({
  prepaid = false,
  serverOrder,
  tipAmount,
  handleApplePay,
  cartEntries: checkoutCartEntries,
}: IUseApplePay) {
  const [requestingPayment, setRequestingPayment] = useState(false);
  const activeRequestRef = useRef<IApplePayRequest | null>(null);

  const { cartEntries: legacyCartEntries } = useCart();
  const cartEntries = checkoutCartEntries ?? legacyCartEntries;
  const cartSource = checkoutCartEntries ? 'checkout' : 'legacy';
  const { user } = useAuthContext();
  const { store } = useStoreContext();
  const { isRestaurantAvailable } = useRestaurantStatusChecker();
  const billingCountry = (user?.details?.isoCountryCode as ISOs) || ISOs.USA;
  const appleConfig = getConfigValue({ key: 'apple', defaultValue: {} });
  const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
  const applePayCapability = useApplePayWebCapability();
  const canUseApplePay = applePayCapability.canUseApplePay;
  const payeeName = fullBrandName();
  const merchantId = appleConfig.merchantId;
  const prepaidMerchantId = appleConfig.prepaidMerchantId;
  const { formatApplePayCartPayload } = useFormatApplePayCartPayload({
    serverOrder,
    cartEntries,
    cartSource,
    tipAmount,
    billingCountry,
  });
  const paymentsNetworks = getConfigValue({
    key: 'apple',
    defaultValue: {},
  }).paymentsNetworks;
  const [queryApplePayValidation] = useApplePayValidationLazyQuery();

  const [scriptHasMounted, setScriptHasMounted] = useState(false);

  useEffect(() => {
    if (canUseApplePay && !scriptHasMounted) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js';
      document.body.appendChild(script);
      setScriptHasMounted(true);
    }
  }, [canUseApplePay, scriptHasMounted]);

  const logLifecycle = useCallback(
    ({ lifecycle, outcome, reason, capability }: IApplePayLifecycleLog) => {
      const logPayload = {
        isPaymentLog: true,
        message: APPLE_PAY_WEB_LIFECYCLE_LOG,
        paymentMethod: 'applePay',
        platform: 'web',
        lifecycle,
        ...(outcome ? { outcome } : {}),
        ...(reason ? { reason } : {}),
        ...(capability
          ? {
              applePaySessionAvailable: capability.applePaySessionAvailable,
              canMakePayments: capability.canMakePayments,
              canMakePaymentsError: capability.canMakePaymentsError,
              enableApplePay: capability.applePayPluginAvailable,
              enableApplePayWeb: capability.isApplePayWebEnabled,
              capabilityReasons: capability.reasons,
            }
          : {}),
      };

      if (outcome === ApplePayOutcome.FAILURE) {
        logger.error(logPayload);
      } else {
        logger.info(logPayload);
      }
    },
    []
  );

  const finishRequest = useCallback(
    (request: IApplePayRequest, result: IApplePayResult, capability?: IApplePayWebCapability) => {
      if (request.settled || (request.ownsSession && activeRequestRef.current !== request)) {
        return;
      }

      request.settled = true;

      if (request.ownsSession) {
        activeRequestRef.current = null;
        setRequestingPayment(false);
      }

      logLifecycle({
        lifecycle: 'terminal',
        outcome: result.outcome,
        reason: result.reason,
        capability,
      });

      const callbackResult =
        result.outcome === ApplePayOutcome.FAILURE && !result.error
          ? { ...result, error: result.reason }
          : result;

      const logCallbackFailure = () => {
        logger.error({
          isPaymentLog: true,
          message: 'Apple Pay Web result callback failed',
          paymentMethod: 'applePay',
          platform: 'web',
          reason: ApplePayFailureReason.UNEXPECTED,
        });
      };

      try {
        Promise.resolve(handleApplePay?.(callbackResult)).catch(logCallbackFailure);
      } catch {
        logCallbackFailure();
      }
    },
    [handleApplePay, logLifecycle]
  );

  const requestApplePayPayment = useCallback(
    ({
      total,
      cartLabels,
      cartPrices,
    }: {
      total: number;
      cartLabels?: string[];
      cartPrices?: number[];
      paymentProcessor?: string | null | undefined;
    }) => {
      const request: IApplePayRequest = {
        ownsSession: false,
        settled: false,
      };

      logLifecycle({ lifecycle: 'request-start' });

      if (activeRequestRef.current) {
        finishRequest(request, {
          outcome: ApplePayOutcome.FAILURE,
          reason: ApplePayFailureReason.REQUEST_ALREADY_ACTIVE,
        });
        return;
      }

      const currentCapability = getApplePayWebCapability({
        applePaySession: window.ApplePaySession,
        applePayPluginAvailable: applePayCapability.applePayPluginAvailable,
        isApplePayWebEnabled: applePayCapability.isApplePayWebEnabled,
      });

      if (!currentCapability.canUseApplePay || !window.ApplePaySession) {
        finishRequest(
          request,
          {
            outcome: ApplePayOutcome.FAILURE,
            reason:
              currentCapability.reasons[0] ?? ApplePayFailureReason.APPLE_PAY_SESSION_UNAVAILABLE,
          },
          currentCapability
        );
        return;
      }

      request.ownsSession = true;
      activeRequestRef.current = request;
      setRequestingPayment(true);

      let lineItems;
      if (cartLabels && cartPrices && cartLabels.length > 0 && cartPrices.length > 0) {
        lineItems = cartLabels.map((label, index) => ({
          label,
          amount: (cartPrices[index] || 0).toString(),
        }));
      } else {
        lineItems = [
          {
            label: payeeName,
            amount: total.toString(),
          },
        ];
      }

      const paymentRequestData: ApplePayJS.ApplePayPaymentRequest = {
        countryCode,
        currencyCode,
        supportedNetworks: paymentsNetworks,
        requiredBillingContactFields: ['postalAddress'],
        merchantCapabilities: ['supports3DS'],
        total: { label: payeeName, amount: (total / 100).toString() },
        lineItems,
      };

      let session: ApplePaySession;
      try {
        session = new window.ApplePaySession(1, paymentRequestData);
        request.session = session;
      } catch {
        finishRequest(request, {
          outcome: ApplePayOutcome.FAILURE,
          reason: ApplePayFailureReason.SESSION_CONSTRUCTION_FAILED,
        });
        return;
      }

      const isCurrentRequest = () => activeRequestRef.current === request && !request.settled;

      const abortSession = () => {
        try {
          session.abort();
        } catch {
          // WebKit cleanup is best-effort; app-level settlement must still happen.
        }
      };

      const completePaymentFailure = () => {
        try {
          session.completePayment(window.ApplePaySession?.STATUS_FAILURE ?? 0);
        } catch {
          // WebKit cleanup is best-effort; app-level settlement must still happen.
        }
      };

      session.oncancel = () => {
        finishRequest(request, {
          outcome: ApplePayOutcome.CANCELLED,
        });
      };

      session.onvalidatemerchant = event => {
        void (async () => {
          if (!isCurrentRequest()) {
            return;
          }

          try {
            const isRestaurantAvailableForOrdering = await isRestaurantAvailable(store);
            if (!isRestaurantAvailableForOrdering) {
              throw new Error(STORE_UNAVAILABLE);
            }
          } catch (error) {
            abortSession();
            finishRequest(request, {
              outcome: ApplePayOutcome.FAILURE,
              reason:
                error instanceof Error && error.message === STORE_UNAVAILABLE
                  ? ApplePayFailureReason.STORE_UNAVAILABLE
                  : ApplePayFailureReason.MERCHANT_VALIDATION_FAILED,
            });
            return;
          }

          if (!isCurrentRequest()) {
            return;
          }

          let merchantSession;
          try {
            merchantSession = await queryApplePayValidation({
              variables: {
                input: {
                  displayName: payeeName,
                  merchantIdentifier: merchantId,
                  validationUrl: event.validationURL,
                  initiative: 'web',
                  initiativeContext: window.location.host,
                },
              },
            });
          } catch {
            abortSession();
            finishRequest(request, {
              outcome: ApplePayOutcome.FAILURE,
              reason: ApplePayFailureReason.MERCHANT_VALIDATION_FAILED,
            });
            return;
          }

          if (!isCurrentRequest()) {
            return;
          }

          const validatedMerchantSession = merchantSession?.data?.validateApplePay;
          if (!validatedMerchantSession) {
            abortSession();
            finishRequest(request, {
              outcome: ApplePayOutcome.FAILURE,
              reason: ApplePayFailureReason.EMPTY_MERCHANT_SESSION,
            });
            return;
          }

          try {
            session.completeMerchantValidation(validatedMerchantSession);
          } catch {
            abortSession();
            finishRequest(request, {
              outcome: ApplePayOutcome.FAILURE,
              reason: ApplePayFailureReason.MERCHANT_VALIDATION_COMPLETION_FAILED,
            });
          }
        })().catch(() => {
          abortSession();
          finishRequest(request, {
            outcome: ApplePayOutcome.FAILURE,
            reason: ApplePayFailureReason.UNEXPECTED,
          });
        });
      };

      session.onpaymentauthorized = event => {
        void (async () => {
          if (!isCurrentRequest()) {
            return;
          }

          try {
            const { billingContact, token } = event.payment;
            const paymentData = token?.paymentData;
            const paymentMethod = token?.paymentMethod;

            if (!billingContact || !paymentData || !paymentData.header || !paymentMethod) {
              completePaymentFailure();
              finishRequest(request, {
                outcome: ApplePayOutcome.FAILURE,
                reason: ApplePayFailureReason.INVALID_AUTHORIZATION_DATA,
              });
              return;
            }

            const {
              signature,
              version,
              data,
              header: { ephemeralPublicKey, publicKeyHash, transactionId },
            } = paymentData;
            const applicationData = '';
            const {
              locality: city,
              addressLines: street = [],
              country,
              postalCode,
              administrativeArea: state,
            } = billingContact;
            const formattedBillingAddress = buildFormattedBillingAddress({
              street: String(street[0]),
              city: String(city),
              state: String(state),
              postalCode: String(postalCode),
              country: String(country),
            });

            const billingAddress = {
              locality: city,
              postalCode: sanitizeAlphanumeric(postalCode),
              region: state,
              streetAddress: street[0],
            };

            const decoratedPaymentData: IApplePayDetails = {
              signature,
              applicationData,
              country: countryCode,
              data,
              decryptAlias: prepaid ? prepaidMerchantId : merchantId,
              ephemeralPublicKey,
              formatted: formattedBillingAddress,
              paymentData: JSON.stringify(paymentData),
              primary: true,
              publicKeyHash,
              transactionId,
              type: 'home',
              version,
              billingAddress,
              PaymentMethodData: {
                paymentType: paymentMethod.network,
                displayName: paymentMethod.displayName,
              },
            };

            try {
              session.completePayment(window.ApplePaySession?.STATUS_SUCCESS ?? 1);
            } catch {
              completePaymentFailure();
              finishRequest(request, {
                outcome: ApplePayOutcome.FAILURE,
                reason: ApplePayFailureReason.AUTHORIZATION_FAILED,
              });
              return;
            }

            finishRequest(request, {
              data: decoratedPaymentData,
              outcome: ApplePayOutcome.SUCCESS,
            });
          } catch {
            completePaymentFailure();
            finishRequest(request, {
              outcome: ApplePayOutcome.FAILURE,
              reason: ApplePayFailureReason.UNEXPECTED,
            });
          }
        })();
      };

      try {
        session.begin();
        logLifecycle({ lifecycle: 'sheet-start' });
      } catch {
        abortSession();
        finishRequest(request, {
          outcome: ApplePayOutcome.FAILURE,
          reason: ApplePayFailureReason.SESSION_START_FAILED,
        });
      }
    },
    [
      applePayCapability.applePayPluginAvailable,
      applePayCapability.isApplePayWebEnabled,
      countryCode,
      currencyCode,
      finishRequest,
      isRestaurantAvailable,
      logLifecycle,
      merchantId,
      payeeName,
      paymentsNetworks,
      prepaid,
      prepaidMerchantId,
      queryApplePayValidation,
      store,
    ]
  );

  return {
    requestingPayment,
    requestApplePayPayment,
    applePayCardDetails,
    formatApplePayCartPayload,
    canUseApplePay,
  };
}
